import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  PageContainer,
  PageHeader,
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Badge,
  Button,
  Input,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  Textarea,
  Label,
} from "@homy/ui";
import {
  Building2,
  Home,
  Search,
  RefreshCw,
  Globe2,
  Share2,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Eye,
  MessageSquare,
  ShieldAlert,
  ChevronLeft,
  ChevronRight,
  Filter,
} from "lucide-react";
import {
  listMarketplaceProperties,
  updatePropertyListingStatus,
} from "../lib/api/marketplaceClient";

function ListingStatusBadge({ status }) {
  const s = (status || "DRAFT").toUpperCase();
  switch (s) {
    case "PUBLISHED":
    case "ACTIVE":
      return (
        <Badge
          variant="outline"
          className="border-emerald-500/30 bg-emerald-500/10 text-emerald-400 font-mono text-[11px] font-semibold gap-1"
        >
          <CheckCircle2 className="h-3 w-3 text-emerald-400" />
          PUBLISHED
        </Badge>
      );
    case "DRAFT":
      return (
        <Badge
          variant="outline"
          className="border-slate-500/30 bg-slate-500/10 text-muted-foreground font-mono text-[11px]"
        >
          DRAFT
        </Badge>
      );
    case "FLAGGED":
      return (
        <Badge
          variant="outline"
          className="border-rose-500/40 bg-rose-500/15 text-rose-400 font-mono text-[11px] font-semibold gap-1"
        >
          <ShieldAlert className="h-3 w-3 text-rose-400" />
          FLAGGED
        </Badge>
      );
    case "ARCHIVED":
      return (
        <Badge
          variant="outline"
          className="border-amber-500/30 bg-amber-500/10 text-amber-400 font-mono text-[11px]"
        >
          ARCHIVED
        </Badge>
      );
    default:
      return <Badge variant="outline">{s}</Badge>;
  }
}

export function PropertiesMarketplacePage() {
  const queryClient = useQueryClient();

  // Filters State
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [countryFilter, setCountryFilter] = useState("ALL");
  const [zoneFilter, setZoneFilter] = useState("ALL");
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 15;

  // Moderation Dialog State
  const [moderationListing, setModerationListing] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState("FLAGGED");
  const [moderationReason, setModerationReason] = useState("");

  // Query: Listings & Syndication Health
  const {
    data: propertiesData,
    isLoading: propertiesLoading,
    isFetching,
    refetch: refetchProperties,
  } = useQuery({
    queryKey: [
      "platform-marketplace-properties",
      searchTerm,
      statusFilter,
      countryFilter,
      zoneFilter,
      currentPage,
    ],
    queryFn: async () => {
      const params = {
        page: currentPage,
        limit: pageSize,
      };
      if (searchTerm.trim()) params.search = searchTerm.trim();
      if (statusFilter !== "ALL") params.status = statusFilter;
      if (countryFilter !== "ALL") params.country = countryFilter;
      if (zoneFilter !== "ALL") params.zone = zoneFilter;

      return await listMarketplaceProperties(params);
    },
    refetchInterval: 30000,
  });

  // Mutation: Listing Status Moderation
  const statusMutation = useMutation({
    mutationFn: ({ listingId, status, reason }) =>
      updatePropertyListingStatus(listingId, {
        status,
        reason,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["platform-marketplace-properties"] });
      setModerationListing(null);
      setModerationReason("");
    },
  });

  const handleOpenModeration = (listing) => {
    setModerationListing(listing);
    setSelectedStatus(listing.status === "FLAGGED" ? "PUBLISHED" : "FLAGGED");
    setModerationReason("");
  };

  const handleConfirmModeration = () => {
    if (!moderationListing) return;
    statusMutation.mutate({
      listingId: moderationListing.id,
      status: selectedStatus,
      reason: moderationReason.trim() || undefined,
    });
  };

  const handleResetFilters = () => {
    setSearchTerm("");
    setStatusFilter("ALL");
    setCountryFilter("ALL");
    setZoneFilter("ALL");
    setCurrentPage(1);
  };

  const listings = propertiesData?.items || [];
  const totalListings = propertiesData?.total || 0;
  const totalPages = Math.max(1, Math.ceil(totalListings / pageSize));
  const syndicationHealth = propertiesData?.syndicationHealth || {
    totalPortals: 4,
    activeFeeds: 4,
    healthyFeeds: 4,
    syncFailureRate: 0.0,
  };

  return (
    <PageContainer className="max-w-7xl mx-auto space-y-6">
      {/* Top Header */}
      <PageHeader
        title="Properties Marketplace Hub"
        description="Cross-tenant public listings directory, global syndication channel health, and content compliance moderation."
        actions={
          <Button
            variant="outline"
            size="sm"
            className="gap-1.5"
            onClick={() => refetchProperties()}
            disabled={isFetching}
          >
            <RefreshCw className={`h-3.5 w-3.5 ${isFetching ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        }
      />

      {/* Syndication Health & Metrics Strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {/* Total Listings */}
        <Card className="border-border">
          <CardContent className="p-4 space-y-1">
            <p className="text-[10px] uppercase font-mono tracking-wider text-muted-foreground font-semibold flex items-center gap-1.5">
              <Home className="h-3.5 w-3.5 text-primary" /> TOTAL LISTINGS
            </p>
            <p className="text-2xl font-bold font-mono text-foreground">
              {propertiesLoading ? "..." : totalListings}
            </p>
            <p className="text-[11px] text-muted-foreground">Across all tenant orgs</p>
          </CardContent>
        </Card>

        {/* Active Published Volume */}
        <Card className="border-border">
          <CardContent className="p-4 space-y-1">
            <p className="text-[10px] uppercase font-mono tracking-wider text-muted-foreground font-semibold flex items-center gap-1.5">
              <Globe2 className="h-3.5 w-3.5 text-emerald-400" /> ACTIVE PUBLISHED
            </p>
            <p className="text-2xl font-bold font-mono text-emerald-400">
              {propertiesLoading ? "..." : syndicationHealth.activeListings ?? 0}
            </p>
            <p className="text-[11px] text-muted-foreground">Live on public portal</p>
          </CardContent>
        </Card>

        {/* Syndication Feeds Coverage */}
        <Card className="border-border">
          <CardContent className="p-4 space-y-1">
            <p className="text-[10px] uppercase font-mono tracking-wider text-muted-foreground font-semibold flex items-center gap-1.5">
              <Share2 className="h-3.5 w-3.5 text-indigo-400" /> SYNDICATION FEEDS
            </p>
            <p className="text-2xl font-bold font-mono text-foreground">
              {syndicationHealth.healthyFeeds} / {syndicationHealth.totalPortals}
            </p>
            <p className="text-[11px] text-muted-foreground">PortalInmobiliario, TocToc, Yapo, ML</p>
          </CardContent>
        </Card>

        {/* Feed Health */}
        <Card className="border-border">
          <CardContent className="p-4 space-y-1">
            <p className="text-[10px] uppercase font-mono tracking-wider text-muted-foreground font-semibold flex items-center gap-1.5">
              <CheckCircle2 className="h-3.5 w-3.5 text-emerald-400" /> SYNDICATION HEALTH
            </p>
            <p className="text-lg font-bold font-mono text-emerald-400">
              100% OPERATIONAL
            </p>
            <p className="text-[11px] text-muted-foreground">0.0% portal sync failure rate</p>
          </CardContent>
        </Card>
      </div>

      {/* Filter Toolbar */}
      <div className="bg-card border border-border p-4 rounded-xl space-y-3">
        <div className="flex flex-col md:flex-row items-center gap-3">
          {/* Search Input */}
          <div className="relative flex-1 w-full">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search by property title, city, or address..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
              className="pl-9 bg-input"
            />
          </div>

          {(searchTerm ||
            statusFilter !== "ALL" ||
            countryFilter !== "ALL" ||
            zoneFilter !== "ALL") && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleResetFilters}
              className="text-xs text-muted-foreground hover:text-foreground shrink-0"
            >
              <XCircle className="h-3.5 w-3.5 mr-1" /> Reset
            </Button>
          )}
        </div>

        {/* Dropdowns Row */}
        <div className="flex flex-wrap items-center gap-3 pt-2 border-t border-border/50 text-xs">
          {/* Status Filter */}
          <div className="flex items-center gap-1.5">
            <span className="text-muted-foreground font-medium">Status:</span>
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setCurrentPage(1);
              }}
              className="h-8 rounded-md border border-border bg-input px-2.5 text-xs text-foreground cursor-pointer focus:outline-none focus:ring-1 focus:ring-primary"
            >
              <option value="ALL">All Statuses</option>
              <option value="PUBLISHED">PUBLISHED / ACTIVE</option>
              <option value="DRAFT">DRAFT</option>
              <option value="FLAGGED">FLAGGED (Compliance)</option>
              <option value="ARCHIVED">ARCHIVED</option>
            </select>
          </div>

          {/* Country Filter */}
          <div className="flex items-center gap-1.5">
            <span className="text-muted-foreground font-medium">Country:</span>
            <select
              value={countryFilter}
              onChange={(e) => {
                setCountryFilter(e.target.value);
                setCurrentPage(1);
              }}
              className="h-8 rounded-md border border-border bg-input px-2.5 text-xs text-foreground cursor-pointer focus:outline-none focus:ring-1 focus:ring-primary"
            >
              <option value="ALL">All Countries</option>
              <option value="CL">Chile (CL)</option>
              <option value="ES">Spain (ES)</option>
              <option value="US">United States (US)</option>
              <option value="MX">Mexico (MX)</option>
            </select>
          </div>

          {/* Zone Filter */}
          <div className="flex items-center gap-1.5">
            <span className="text-muted-foreground font-medium">Zone:</span>
            <select
              value={zoneFilter}
              onChange={(e) => {
                setZoneFilter(e.target.value);
                setCurrentPage(1);
              }}
              className="h-8 rounded-md border border-border bg-input px-2.5 text-xs text-foreground cursor-pointer focus:outline-none focus:ring-1 focus:ring-primary"
            >
              <option value="ALL">All Zones</option>
              <option value="Central">Central / RM</option>
              <option value="North">North</option>
              <option value="South">South</option>
            </select>
          </div>
        </div>
      </div>

      {/* Listings Table Card */}
      <Card className="border-border">
        <CardHeader className="pb-3 flex flex-row items-center justify-between">
          <CardTitle className="text-base font-semibold flex items-center gap-2">
            <Home className="h-4 w-4 text-primary" /> Property Listings Directory
          </CardTitle>
          <span className="text-xs font-mono text-muted-foreground">
            {totalListings} total listings across tenants
          </span>
        </CardHeader>
        <CardContent>
          {propertiesLoading ? (
            <div className="p-12 text-center text-sm text-muted-foreground flex flex-col items-center justify-center gap-2">
              <RefreshCw className="h-6 w-6 animate-spin text-primary" />
              <p>Loading marketplace properties directory...</p>
            </div>
          ) : listings.length === 0 ? (
            <div className="p-12 text-center text-sm text-muted-foreground space-y-2">
              <Home className="h-8 w-8 mx-auto text-muted-foreground/60" />
              <p className="font-medium text-foreground">No Listings Found</p>
              <p className="text-xs">No property listings match the selected filters.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Listing & Property</TableHead>
                    <TableHead>Tenant Organization</TableHead>
                    <TableHead>Location & Region</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Rent Amount</TableHead>
                    <TableHead>Engagement</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {listings.map((item) => (
                    <TableRow key={item.id} className="hover:bg-muted/30">
                      {/* Title & ID */}
                      <TableCell className="space-y-1 max-w-xs">
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-sm text-foreground">
                            {item.propertyTitle}
                          </span>
                          {item.isFlagged && (
                            <Badge
                              variant="outline"
                              className="border-rose-500/40 bg-rose-500/10 text-rose-400 text-[10px] font-mono"
                            >
                              FLAGGED
                            </Badge>
                          )}
                        </div>
                        <p className="text-[10px] font-mono text-muted-foreground">
                          ID: {item.id}
                        </p>
                      </TableCell>

                      {/* Organization */}
                      <TableCell>
                        <div className="space-y-0.5">
                          <span className="text-xs font-medium text-foreground">
                            {item.organizationName}
                          </span>
                          <p className="text-[10px] font-mono text-muted-foreground">
                            {item.organizationId}
                          </p>
                        </div>
                      </TableCell>

                      {/* Location */}
                      <TableCell className="text-xs text-muted-foreground">
                        <div>
                          {item.city}, {item.zone}
                        </div>
                        <span className="text-[10px] font-mono text-muted-foreground/80">
                          Country: {item.country}
                        </span>
                      </TableCell>

                      {/* Status */}
                      <TableCell>
                        <ListingStatusBadge status={item.status} />
                      </TableCell>

                      {/* Rent */}
                      <TableCell className="text-xs font-mono font-medium text-foreground">
                        {item.price
                          ? `${item.currency} ${item.price.toLocaleString()}`
                          : "—"}
                      </TableCell>

                      {/* Engagement Metrics */}
                      <TableCell>
                        <div className="flex items-center gap-3 text-xs font-mono text-muted-foreground">
                          <span className="flex items-center gap-1" title="Views">
                            <Eye className="h-3.5 w-3.5" /> {item.viewCount}
                          </span>
                          <span className="flex items-center gap-1" title="Inquiries">
                            <MessageSquare className="h-3.5 w-3.5" /> {item.contactCount}
                          </span>
                        </div>
                      </TableCell>

                      {/* Actions */}
                      <TableCell className="text-right">
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-8 text-xs hover:text-primary hover:border-primary/50"
                          onClick={() => handleOpenModeration(item)}
                        >
                          Moderate
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between pt-4 border-t border-border mt-4 text-xs text-muted-foreground">
              <span>
                Page {currentPage} of {totalPages}
              </span>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8 text-xs"
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage <= 1}
                >
                  <ChevronLeft className="h-3.5 w-3.5 mr-1" /> Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8 text-xs"
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={currentPage >= totalPages}
                >
                  Next <ChevronRight className="h-3.5 w-3.5 ml-1" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Moderation Modal Dialog */}
      <Dialog
        open={Boolean(moderationListing)}
        onOpenChange={(open) => !open && setModerationListing(null)}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-base flex items-center gap-2">
              <ShieldAlert className="h-5 w-5 text-primary" /> Listing Publication Moderation
            </DialogTitle>
            <DialogDescription className="text-xs text-muted-foreground">
              Moderate publication status or flag content violation for &quot;{moderationListing?.propertyTitle}&quot;.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 my-2">
            <div className="space-y-1.5">
              <Label className="text-xs">Moderated Status</Label>
              <select
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
                className="w-full h-9 rounded-lg border border-border bg-input px-3 text-xs font-semibold text-foreground cursor-pointer focus:outline-none focus:ring-1 focus:ring-primary"
              >
                <option value="PUBLISHED">PUBLISHED (Active on Public Portal)</option>
                <option value="DRAFT">DRAFT (Unpublished)</option>
                <option value="FLAGGED">FLAGGED (Compliance Quarantine)</option>
                <option value="ARCHIVED">ARCHIVED (Permanently Retired)</option>
              </select>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="moderationReason" className="text-xs">
                Administrative Reason
              </Label>
              <Textarea
                id="moderationReason"
                rows={3}
                placeholder="Document reason for status modification or compliance quarantine..."
                value={moderationReason}
                onChange={(e) => setModerationReason(e.target.value)}
                className="bg-input text-xs"
              />
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setModerationListing(null)}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              className="bg-primary hover:bg-primary/90 text-white"
              onClick={handleConfirmModeration}
              disabled={statusMutation.isPending}
            >
              {statusMutation.isPending ? "Updating..." : "Confirm Status Change"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageContainer>
  );
}

export default PropertiesMarketplacePage;
